/**
 * Recently viewed products.
 *
 * The ids live in localStorage, most recent first. The markup is fetched from
 * sections/munne-recently-viewed.liquid through the Section Rendering API on
 * the /search route, which is the only route that gives the section a
 * populated `search` object.
 */
class MunneRecentlyViewed extends HTMLElement {
  static STORAGE_KEY = 'munne:recently-viewed';
  static STORED_LIMIT = 20;

  connectedCallback() {
    // The section is also present in the fetched markup; only the copy on the
    // page should fetch anything.
    if (this.dataset.rendered === 'true') return;

    const stored = this.read();
    const currentId = this.dataset.currentProductId;
    const others = stored.filter((id) => id !== currentId);

    if (currentId) {
      this.write([currentId, ...others].slice(0, MunneRecentlyViewed.STORED_LIMIT));
    }

    const limit = parseInt(this.dataset.limit, 10) || 8;
    const ids = others.slice(0, limit);

    if (ids.length === 0) return;

    this.render(ids);
  }

  read() {
    try {
      const raw = window.localStorage.getItem(MunneRecentlyViewed.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string' && id) : [];
    } catch (error) {
      // Private browsing, blocked storage or corrupted value: no history.
      return [];
    }
  }

  write(ids) {
    try {
      window.localStorage.setItem(MunneRecentlyViewed.STORAGE_KEY, JSON.stringify(ids));
    } catch (error) {
      // Nothing to do: the list is a convenience, not a requirement.
    }
  }

  async render(ids) {
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${this.dataset.searchUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}&type=product&q=${encodeURIComponent(query)}`;

    let markup;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      markup = await response.text();
    } catch (error) {
      return;
    }

    const section = new DOMParser().parseFromString(markup, 'text/html').querySelector('munne-recently-viewed');
    if (!section || section.innerHTML.trim().length === 0) return;

    this.reorder(section, ids);

    this.dataset.rendered = 'true';
    this.innerHTML = section.innerHTML;
  }

  /** Search answers in its own order; put the cards back in most-recent-first. */
  reorder(section, ids) {
    const list = section.querySelector('[id^="Slider-"]');
    if (!list) return;

    // Direct children only: the favourite button inside every card carries a
    // data-product-id too, and appendChild below would tear it out of its card
    // and drop it straight into the list.
    const slides = Array.from(list.querySelectorAll(':scope > [data-product-id]'));
    slides
      .slice()
      .sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId))
      .forEach((slide, index) => {
        slide.id = `Slide-${this.dataset.sectionId}-${index + 1}`;
        list.appendChild(slide);
      });
  }
}

customElements.define('munne-recently-viewed', MunneRecentlyViewed);
